import { AlertTriangle } from "lucide-react";
import { ApiError } from "@/api/client";
import { Button, EmptyState } from "@/design-system/components/ui";

/**
 * Honest states for a read that failed.
 *
 * <p>🔴 <b>The defect this exists to stop.</b> Across the Buyer CRM every read
 * branched on `isLoading` and then on the DATA — so a request that failed fell
 * through to the empty branch and rendered a confident claim about the record:
 * "The queue is clear. Every buyer has an owner." to a manager whose queue
 * simply had not loaded; "Listing not found. It may have been removed." for a
 * listing that exists; a package panel that vanished entirely. Every one of
 * those is a statement about the world, made on the strength of a failed
 * network request, with nothing on screen to say so and no way to retry.</p>
 *
 * <p>Three states, never folded together:</p>
 * <ul>
 *   <li><b>Empty</b> — the server answered, and the answer is nothing.</li>
 *   <li><b>Not found</b> — the server answered 404. A fact about the record.</li>
 *   <li><b>Failed</b> — the request did not complete. A fact about the REQUEST,
 *       and the reader must be told which kind of fact they are looking at.</li>
 * </ul>
 *
 * <p>Every failure state carries a retry wired to the query's own `refetch`, so
 * recovery does not cost a full page reload — and on this app it would have to,
 * because the shared QueryClient runs at `staleTime: 5min` with
 * `refetchOnWindowFocus: false`, so a failed read has no other route back.</p>
 */

/**
 * Did the server say this record does not exist, or did the request merely
 * fail?
 *
 * <p>Only a 404 licenses a claim about the record. Anything else — a network
 * drop, a 500, a 403 — is a fact about the request, and the two must not be
 * rendered as the same sentence. Where a caller cannot get a clean answer, the
 * honest "couldn't load" is the one to prefer: it is never wrong, whereas
 * "removed" can be.</p>
 */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

interface QueryErrorProps {
  /**
   * The noun phrase completing "Couldn't load …" — e.g. `"the assignment
   * queue"`, `"this listing"`. Named rather than generic so a page with several
   * independent reads says WHICH one failed.
   */
  what: string;
  /** The query's own `refetch`. */
  onRetry: () => void;
  /** `isFetching` on the same query, so a retry in flight reads as one. */
  retrying?: boolean;
}

/** The sentence both variants share — one place, so they cannot drift. */
const NOT_A_CLAIM =
  "The request failed, so nothing here is a statement about what is on file.";

/**
 * Block-level failure — for a whole page body, or the slot a list's table
 * would occupy.
 *
 * <p>Carries the same visual weight as the empty state it replaces, because a
 * failure that renders lighter than "nothing here" reads as nothing here.</p>
 */
export function QueryErrorState({ what, onRetry, retrying }: QueryErrorProps) {
  return (
    <EmptyState
      icon={AlertTriangle}
      title={`Couldn't load ${what}`}
      description={NOT_A_CLAIM}
      variant="noResults"
      action={{ label: retrying ? "Retrying…" : "Try again", onClick: onRetry }}
    />
  );
}

/**
 * Compact failure — for inside a section that already has a heading, where the
 * block variant's second heading would be a duplicate.
 */
export function InlineQueryError({ what, onRetry, retrying }: QueryErrorProps) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-space-3 rounded-md border border-status-error bg-white-warm px-space-3 py-space-2"
    >
      <AlertTriangle
        size={14}
        className="text-status-error shrink-0"
        aria-hidden
      />
      <p className="text-small text-onyx m-0 flex-1 min-w-[12rem]">
        Couldn&apos;t load {what}. {NOT_A_CLAIM}
      </p>
      <Button size="sm" variant="secondary" onClick={onRetry} disabled={retrying}>
        {retrying ? "Retrying…" : "Try again"}
      </Button>
    </div>
  );
}
